<template>
  <PublicShell :color="data && data.brand ? data.brand.color : null">
    <div v-if="loading" class="rvw-loading" role="status"><span class="pb-sr-only">Loading…</span>
      <span class="pb-skeleton rvw-sk1"></span><span class="pb-skeleton rvw-sk2"></span></div>
    <div v-else-if="failed" class="pb-alert is-error pub-failed" role="alert"><i class="mdi mdi-wifi-off pb-ico" aria-hidden="true"></i><span>We couldn't open this page. Check your connection and try again.</span><button type="button" class="pb-button sm outline" @click="reload">Try again</button></div>
    <div v-else-if="gone" class="rvw-message">
      <span class="pb-empty-icon" aria-hidden="true"><i class="mdi mdi-link-variant-off"></i></span>
      <h1 class="rvw-title display-font">This link isn't active</h1>
      <p class="pb-body">{{ gone }}</p>
    </div>
    <template v-else-if="data">
      <header class="rvw-head">
        <div class="rvw-agency">
          <img v-if="senderLogo" :src="senderLogo" alt="" class="rvw-logo">
          <strong>{{ agencyName || data.brand.name }}</strong>
        </div>
        <h1 class="rvw-title display-font">Testimonials waiting for {{ possessive(data.brand.name) }} approval</h1>
        <p class="pb-body">Hello {{ withStop(data.reviewer.name) }} Each card below shows exactly how a testimonial will appear on {{ possessive(data.brand.name) }} website. Approve it, or ask for a change.</p>
      </header>

      <div v-if="!items.length" class="rvw-done">
        <span class="pb-empty-icon" aria-hidden="true"><i class="mdi mdi-check-all"></i></span>
        <h2 class="rvw-subtitle display-font">All done</h2>
        <p class="pb-body">{{ decided ? 'Thank you! ' + publisher + ' will publish the testimonials you approved.' : 'Nothing is waiting for your approval right now.' }}</p>
      </div>

      <ul v-else class="rvw-list">
        <li v-for="item in items" :key="item.id" class="rvw-item">
          <TestimonialCard :item="cardOf(item)" />
          <p class="pb-muted rvw-shown"><i class="mdi mdi-eye-outline" aria-hidden="true"></i>
            Shown publicly: {{ item.shown.length ? listText(item.shown) : 'the words only, without a name' }}.</p>
          <div v-if="rejecting === item.id" class="rvw-reject">
            <label class="pb-label" :for="'rvw-c-' + item.id">What should change?</label>
            <textarea :id="'rvw-c-' + item.id" v-model="comment" class="pb-textarea" rows="3" maxlength="1000"></textarea>
            <div class="pb-actions"><button type="button" class="pb-button" :disabled="busy || !comment.trim()" @click="decide(item, 'rejected')">Send to {{ agencyName || 'your agency' }}</button>
              <button type="button" class="pb-button ghost" @click="rejecting = null">Cancel</button></div>
          </div>
          <div v-else class="pb-actions rvw-actions">
            <button type="button" class="rvw-approve" :disabled="busy" @click="decide(item, 'approved')"><i class="mdi mdi-check" aria-hidden="true"></i>Approve</button>
            <button type="button" class="pb-button outline" :disabled="busy" @click="rejecting = item.id; comment = ''">Ask for a change</button>
          </div>
          <p v-if="errors[item.id]" class="pb-field-error" role="alert">{{ errors[item.id] }}</p>
        </li>
      </ul>
    </template>
  </PublicShell>
</template>

<script>
import PublicShell from '../components/PublicShell.vue';
import TestimonialCard from '../components/TestimonialCard.vue';
import { get, post, message } from '../api';
import { imageUrl, publicApiBase } from '../publicUrls';
import { toast } from '../lib/toast';
import { listText, possessive, withStop } from '../lib/format';

export default {
  name: 'PublicReviewView',
  components: { PublicShell, TestimonialCard },
  data: () => ({ data: null, items: [], loading: true, gone: '', failed: false, busy: false, rejecting: null, comment: '', decided: 0, errors: {} }),
  computed: {
    token() { return String(this.$route.params.token || ''); },
    // The agency's own name and logo once it has set them up as an agency. Until then the API sends no agency, and
    // the page shows the client's own brand: never a workspace named after another client.
    agencyName() { return this.data && this.data.agency ? this.data.agency.name : ''; },
    publisher() { return this.agencyName || 'Your agency'; },
    senderLogo() {
      if (!this.data) return '';
      const logo = this.data.agency ? this.data.agency.logo : this.data.brand.logo;
      return logo ? imageUrl(logo) : '';
    }
  },
  async mounted() {
    try {
      this.data = await get('/public/review/' + encodeURIComponent(this.token));
      this.items = this.data.items;
      this.decided = this.data.decided || 0;
      document.title = this.data.brand.name + ' · Testimonials for your approval';
    } catch (error) {
      const status = error.response ? error.response.status : 0;
      if (status === 404 || status === 429) this.gone = message(error, 'This review link is no longer active. Ask your agency for a new one.');
      else this.failed = true;
    } finally { this.loading = false; }
  },
  methods: {
    withStop,
    possessive,
    listText,
    reload() { window.location.reload(); },
    // Waiting photos are not public yet; this link serves them to the reviewer only.
    cardOf(item) {
      const card = Object.assign({}, item.testimonial);
      if (card.photo) card.photo_url = publicApiBase() + '/apps/praiseboard/public/review/' + encodeURIComponent(this.token) + '/images/' + encodeURIComponent(card.photo);
      return card;
    },
    async decide(item, decision) {
      this.busy = true;
      this.$set(this.errors, item.id, '');
      try {
        await post('/public/review/' + encodeURIComponent(this.token) + '/decisions', { submission_id: item.id, decision, comment: decision === 'rejected' ? this.comment.trim() : null });
        this.items = this.items.filter(entry => entry.id !== item.id);
        this.decided += 1;
        this.rejecting = null;
        toast(decision === 'approved' ? 'Approved. Thank you.' : 'Sent. ' + this.publisher + ' will see your comment.');
      } catch (error) { this.$set(this.errors, item.id, message(error)); } finally { this.busy = false; }
    }
  }
};
</script>

<style scoped>
.rvw-head{max-width:720px;margin-bottom:28px}
.rvw-agency{display:flex;align-items:center;gap:12px;margin-bottom:22px;font-size:16px}
.rvw-logo{height:44px;max-width:160px;object-fit:contain}
.rvw-title{font:500 34px/1.2 var(--font-display)!important;margin:0 0 12px;letter-spacing:-.5px}
.rvw-subtitle{font:500 26px/1.2 var(--font-display)!important;margin:0}
.rvw-list{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(min(420px,100%),1fr));gap:22px}
.rvw-item{display:flex;flex-direction:column;gap:12px;padding:16px;border:1px solid #E6DED2;border-radius:18px;background:#FBF8F3}
.rvw-shown{display:flex;gap:6px;align-items:flex-start;margin:0}
.rvw-actions{margin-top:auto}
.rvw-approve{display:inline-flex;align-items:center;gap:8px;min-height:44px;padding:10px 18px;border:0;border-radius:10px;background:var(--brand);
  color:var(--brand-on);font:600 14.5px var(--font-body);cursor:pointer}
.rvw-approve:disabled{opacity:.6}
.rvw-approve:focus-visible{outline:2px solid #1F2328;outline-offset:2px}
.rvw-reject{display:grid;gap:8px}
.rvw-done,.rvw-message{text-align:center;padding:48px 24px;background:#fff;border:1px solid #E6DED2;border-radius:20px}
.rvw-done .pb-body,.rvw-message .pb-body{max-width:440px;margin:10px auto 0}
.rvw-loading{display:grid;gap:14px}
.rvw-sk1{height:30px;width:200px}
.rvw-sk2{height:220px;width:100%;border-radius:18px}
@media(max-width:600px){.rvw-title{font-size:27px!important}}
</style>
